import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getExecution, wordpressApi } from '../../../api';
import type {
  ExecutionDetailResponse,
  ExecutionStep,
  WordPressPatchDecision,
  WordPressPatchItem,
  WordPressSecurityFinding,
  WordPressSoftwareItem,
} from '../../../api';
import { ErrorNotice, LoadingRows } from '../../../components/ui';
import { LevelBadge, PanelCard, ProjectSection, QuietEmpty, SectionHeader } from '../../../components/project/ui';
import type { Level } from '../../../components/project/projectHealth';
import { relativeTime } from '../../../components/project/projectHealth';
import {
  compatibilityVerdictLabel,
  executionStatusLabel,
  labelFor,
  patchDecisionLabel,
  patchPolicyLabel,
  securitySeverityLabel,
  updateCategoryLabel,
} from '../../../labels';
import { Callout, Checkbox, Dialog, Input, Table } from '../../../components/primitives';
import { EvidenceActions, NextSteps, OperationSummary, SummaryFact, SummaryFacts } from '../../../components/operation/OperationSummary';
import type { NextStep, SummaryTone } from '../../../components/operation/OperationSummary';
import { actionLabel } from '../../../components/operation/actionLabels';
import { securityCoverage, softwareCoverage } from '../../../components/project/wordpressHealth';
import { useProject } from '../ProjectContext';

const kindWord: Record<string, string> = { core: 'Core', plugin: 'Plugin', theme: 'Theme' };

function itemKey(item: { kind: string; slug: string }): string {
  return `${item.kind}:${item.slug}`;
}

function categoryLevel(category: string | undefined): Level {
  if (category === 'security') return 'critical';
  if (category === 'major' || category === 'unknown' || !category) return 'warning';
  return 'ok';
}

const severityLevel: Record<string, Level> = { critical: 'critical', high: 'critical', medium: 'warning', low: 'unknown', info: 'unknown' };

/**
 * WordPressUpdatesSection is updates and security in one place: what is
 * installed, what can be updated and why each update is or is not covered
 * by the patch policy, and the hardening findings. Updates only ever run
 * when an operator starts them here.
 */
export function WordPressUpdatesSection() {
  const project = useProject();
  const site = project.wordpressSite.data;

  if (project.wordpressSite.loading && !site) {
    return (
      <ProjectSection>
        <PanelCard>
          <LoadingRows label="Loading updates" rows={5} rowHeight="32px" />
        </PanelCard>
      </ProjectSection>
    );
  }
  if (!site) {
    return (
      <ProjectSection>
        <QuietEmpty testId="no-wordpress-site">
          No WordPress site
          <Link className="button button--primary button--small quiet-empty__action" to={project.sectionPath('site')}>
            Connect the site
          </Link>
        </QuietEmpty>
      </ProjectSection>
    );
  }

  return (
    <ProjectSection>
      <SoftwareCard />
      <SecurityCard findings={site.security?.findings ?? []} />
    </ProjectSection>
  );
}

function SoftwareCard() {
  const project = useProject();
  const site = project.wordpressSite.data!;
  const report = site.software;
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [review, setReview] = useState<WordPressPatchDecision[] | null>(null);
  const [reviewing, setReviewing] = useState(false);
  const [runId, setRunId] = useState<string | null>(null);

  const items = useMemo(() => report?.items ?? [], [report]);
  const updatable = items.filter((item) => item.target_version);
  const shown = showAll ? items : updatable;

  async function check() {
    setChecking(true);
    setError(null);
    try {
      await wordpressApi.refreshSoftware(site.id);
      project.refresh();
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setChecking(false);
    }
  }

  function toggle(item: WordPressSoftwareItem) {
    setSelected((current) => {
      const next = new Set(current);
      const key = itemKey(item);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  const chosen: WordPressPatchItem[] = updatable
    .filter((item) => selected.has(itemKey(item)))
    .map((item) => ({ kind: item.kind, slug: item.slug, target_version: item.target_version! }));

  async function startReview() {
    setReviewing(true);
    setError(null);
    try {
      const res = await wordpressApi.evaluatePatch(site.id, chosen);
      setReview(res.decisions ?? []);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setReviewing(false);
    }
  }

  // A checked feed is a fact in the header; only the two ways the check
  // could not cover vulnerabilities are notes of their own.
  const feed = report
    ? report.vulnerability_state === 'unavailable'
      ? `Vulnerability feed unavailable${report.vulnerability_error ? `: ${report.vulnerability_error}` : ''}`
      : report.vulnerability_state === 'checked'
        ? null
        : 'Vulnerabilities not checked: no feed configured'
    : null;

  return (
    <PanelCard labelledBy="wp-updates-title" testId="wp-updates">
      <SectionHeader
        id="wp-updates-title"
        title="Updates"
        description={
          report
            ? [
                `Checked ${relativeTime(site.software_checked_at)}`,
                report.vulnerability_state === 'checked' ? `Vulnerabilities via ${report.vulnerability_source || 'configured feed'}` : null,
                `Policy: ${patchPolicyLabel[site.settings.patch_policy?.mode || 'manual_approval'] ?? 'Approve every update'}`,
              ]
                .filter(Boolean)
                .join(' · ')
            : 'Not checked yet'
        }
        actions={
          <button type="button" className="button button--secondary" onClick={check} disabled={checking} data-testid="wp-check-updates">
            {checking ? 'Checking' : 'Check now'}
          </button>
        }
      />
      <ErrorNotice error={error} />
      {feed && report?.vulnerability_state === 'unavailable' ? (
        <Callout
          tone="warning"
          title="Not fully checked"
          testId="wp-feed-unavailable"
          actions={
            <button type="button" className="button button--secondary button--small" onClick={check} disabled={checking}>
              {checking ? 'Checking' : actionLabel.retry}
            </button>
          }
        >
          {feed}
        </Callout>
      ) : feed ? (
        <p className="panel__note muted">{feed}</p>
      ) : null}

      {runId ? <PatchRunStatus executionId={runId} onFinished={() => project.refresh()} onDismiss={() => setRunId(null)} /> : null}

      {!report ? null : shown.length === 0 ? (
        <QuietEmpty testId="wp-no-updates">
          {/* The note above says what the check did not cover; this line only says what it found. */}
          {softwareCoverage(site).complete ? 'Up to date' : 'No updates found'} · {items.length} installed
        </QuietEmpty>
      ) : (
        <Table label="Installed software and available updates" compact className="wp-software">
          <thead>
            <tr>
              <th scope="col">
                <span className="visually-hidden">Select</span>
              </th>
              <th scope="col">Name</th>
              <th scope="col">Installed</th>
              <th scope="col">Available</th>
              <th scope="col">Kind of update</th>
              <th scope="col">Declared compatibility</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((item) => {
              const key = itemKey(item);
              const vulns = item.vulnerabilities ?? [];
              return (
                <tr key={key}>
                  <td>
                    {item.target_version ? (
                      <Input
                        type="checkbox"
                        checked={selected.has(key)}
                        onChange={() => toggle(item)}
                        aria-label={`Select ${item.name || item.slug} for updating`}
                      />
                    ) : null}
                  </td>
                  <td>
                    <strong>{item.name || item.slug}</strong>
                    <div className="muted wp-software__meta">
                      {kindWord[item.kind] ?? item.kind}
                      {item.status ? ` · ${item.status}` : ''}
                      {item.kind !== 'core' ? (
                        <>
                          {' · '}
                          <code>{item.slug}</code>
                        </>
                      ) : null}
                    </div>
                    {vulns.length > 0 ? (
                      <ul className="wp-vulns">
                        {vulns.map((vuln) => (
                          <li key={vuln.id}>
                            <LevelBadge level="critical">{vuln.cvss_rating ? `${vuln.cvss_rating} vulnerability` : 'Vulnerability'}</LevelBadge>{' '}
                            {vuln.title}
                            {vuln.cve?.length ? <span className="muted"> ({vuln.cve.join(', ')})</span> : null}
                            {vuln.patched_in?.length ? <span className="muted"> — fixed in {vuln.patched_in.join(', ')}</span> : <span className="muted"> — no fixed version published</span>}
                            <span className="muted"> · {vuln.provider}</span>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </td>
                  <td className="tabular-nums">{item.version || <span className="muted">not known</span>}</td>
                  <td className="tabular-nums">{item.target_version || <span className="muted">—</span>}</td>
                  <td>
                    {item.target_version ? (
                      <LevelBadge level={categoryLevel(item.category)}>{labelFor(updateCategoryLabel, item.category || 'unknown')}</LevelBadge>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                  <td>
                    {item.target_version ? (
                      <span title={(item.compatibility.reasons ?? []).join('; ') || undefined}>
                        {labelFor(compatibilityVerdictLabel, item.compatibility.verdict)}
                        {item.compatibility.verdict === 'incompatible' && item.compatibility.reasons?.length ? (
                          <span className="muted"> — {item.compatibility.reasons.join('; ')}</span>
                        ) : null}
                      </span>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}

      {report ? (
        <div className="panel__footer-row">
          <button type="button" className="link-button" onClick={() => setShowAll((value) => !value)} aria-expanded={showAll}>
            {showAll ? 'Show only available updates' : `Show all ${items.length} installed`}
          </button>
          <button
            type="button"
            className="button button--primary"
            disabled={chosen.length === 0 || reviewing || runId !== null}
            onClick={startReview}
            data-testid="wp-review-updates"
          >
            {reviewing ? 'Checking the policy' : chosen.length === 0 ? 'Select updates to apply' : `Review ${chosen.length} ${chosen.length === 1 ? 'update' : 'updates'}`}
          </button>
        </div>
      ) : null}

      {review ? (
        <PatchReviewDialog
          siteId={site.id}
          decisions={review}
          onClose={() => setReview(null)}
          onStarted={(executionId) => {
            setReview(null);
            setSelected(new Set());
            setRunId(executionId);
          }}
        />
      ) : null}
    </PanelCard>
  );
}

const decisionLevel: Record<string, Level> = { allowed_by_policy: 'ok', approval_required: 'warning', blocked: 'critical' };

function PatchReviewDialog({
  siteId,
  decisions,
  onClose,
  onStarted,
}: {
  siteId: string;
  decisions: WordPressPatchDecision[];
  onClose: () => void;
  onStarted: (executionId: string) => void;
}) {
  const [approved, setApproved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const runnable = decisions.filter((decision) => decision.decision.decision !== 'blocked');
  const needsApproval = runnable.some((decision) => decision.decision.decision === 'approval_required');

  async function start() {
    setBusy(true);
    setError(null);
    try {
      const res = await wordpressApi.startPatch(
        siteId,
        runnable.map((decision) => decision.item),
        approved,
      );
      onStarted(res.execution.id);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      setBusy(false);
    }
  }

  return (
    <Dialog
      open
      size="lg"
      className="wp-review"
      title={`Apply ${runnable.length} ${runnable.length === 1 ? 'update' : 'updates'}?`}
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <button type="button" className="button button--secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="button button--primary"
            onClick={start}
            disabled={busy || runnable.length === 0 || (needsApproval && !approved)}
            data-testid="wp-start-patch"
          >
            {busy ? 'Starting' : 'Apply updates'}
          </button>
        </>
      }
    >
      <p className="dialog__text">
        Commerce Ops checks the site first, takes a database checkpoint and keeps a copy of each package. If WordPress
        or WooCommerce fails its checks afterwards, the previous package files are put back. The database is never
        rolled back automatically: once the run has updated the database, nothing is put back, and the checkpoint is
        there for you to decide.
      </p>
      <ul className="wp-review__list">
        {decisions.map((decision) => (
          <li key={itemKey(decision.item)}>
            <div>
              <strong>{decision.software?.name || decision.item.slug}</strong>{' '}
              <span className="muted">
                {decision.software?.version} → {decision.item.target_version}
              </span>
              <p className="muted">{decision.decision.reason}</p>
            </div>
            <LevelBadge level={decisionLevel[decision.decision.decision] ?? 'unknown'}>
              {labelFor(patchDecisionLabel, decision.decision.decision)}
            </LevelBadge>
          </li>
        ))}
      </ul>
      {runnable.length < decisions.length ? (
        <Callout tone="warning">Blocked updates are left out of this run.</Callout>
      ) : null}
      {needsApproval ? (
        <Checkbox
          checked={approved}
          onChange={(event) => setApproved(event.target.checked)}
          data-testid="wp-approve"
          label="I approve the updates marked “Needs approval”."
        />
      ) : null}
      <ErrorNotice error={error} />
    </Dialog>
  );
}

const terminal = new Set(['completed', 'failed', 'cancelled', 'rolled_back']);

/** Steps that look at the shop after the update, by the key the patch run gives them. */
const shopChecks: { key: string; label: string }[] = [
  { key: 'verify_wordpress', label: 'WordPress and WooCommerce checks' },
  { key: 'http_smoke', label: 'Storefront check from outside' },
];

/**
 * What the run observed about the shop afterwards, only from the steps that
 * look at it. A step that did not run is "not checked", never "fine".
 */
export function shopAfterUpdate(steps: ExecutionStep[]): { text: string; checked: boolean } {
  const results = shopChecks.map(({ key, label }) => ({ label, step: steps.find((step) => step.step_key === key) }));
  const done = results.filter((entry) => entry.step?.status === 'succeeded' || entry.step?.status === 'failed');
  if (done.length === 0) {
    return { text: 'Not checked · the run stopped before these checks', checked: false };
  }
  const parts = results.map(({ label, step }) => {
    if (step?.status === 'succeeded') return `${label} passed`;
    if (step?.status === 'failed') return `${label} failed`;
    return `${label} did not run`;
  });
  return { text: parts.join(' · '), checked: true };
}

function PatchRunStatus({
  executionId,
  onFinished,
  onDismiss,
}: {
  executionId: string;
  onFinished: () => void;
  onDismiss: () => void;
}) {
  const { sectionPath } = useProject();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<ExecutionDetailResponse | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const finished = useRef(false);
  // The parent passes a fresh callback each render; polling must not
  // restart because of that.
  const onFinishedRef = useRef(onFinished);
  onFinishedRef.current = onFinished;

  useEffect(() => {
    let alive = true;
    let timer: number | undefined;
    async function poll() {
      try {
        const next = await getExecution(executionId);
        if (!alive) return;
        setDetail(next);
        setError(null);
        if (terminal.has(next.execution.status)) {
          if (!finished.current) {
            finished.current = true;
            onFinishedRef.current();
          }
          return;
        }
      } catch (err) {
        if (alive) setError(err instanceof Error ? err : new Error(String(err)));
      }
      timer = window.setTimeout(poll, 3000);
    }
    void poll();
    return () => {
      alive = false;
      window.clearTimeout(timer);
    };
  }, [executionId]);

  const status = detail?.execution.status ?? 'pending';
  const done = terminal.has(status);
  const steps = (detail?.steps ?? []).slice().sort((a, b) => a.sequence - b.sequence);
  const failedStep = steps.find((step) => step.status === 'failed');
  const runPath = sectionPath('deployments', { run: executionId, view: 'runs' });
  const updates = steps.filter((step) => step.action_type === 'update');
  const updated = updates.filter((step) => step.status === 'succeeded').length;
  // Succeeded and skipped steps fold into one line; the rest stay visible.
  const quiet = steps.filter((step) => step.status === 'succeeded' || step.status === 'skipped');
  const attention = steps.filter((step) => !quiet.includes(step));
  const shop = shopAfterUpdate(steps);

  const title =
    status === 'completed'
      ? 'Update run succeeded'
      : status === 'failed'
        ? 'Update run failed'
        : status === 'rolled_back'
          ? 'Update run was rolled back'
          : status === 'cancelled'
            ? 'Update run was cancelled'
            : `Update run: ${labelFor(executionStatusLabel, status)}`;
  const tone: SummaryTone =
    status === 'completed' ? 'success' : status === 'failed' ? 'danger' : status === 'rolled_back' ? 'warning' : status === 'cancelled' ? 'neutral' : 'progress';
  const nextSteps: NextStep[] = (detail?.interpretation?.actions ?? []).map((action) => ({ label: action.label, detail: action.detail, command: action.command }));
  const needsNext = status === 'failed' || status === 'rolled_back';

  return (
    <OperationSummary
      tone={tone}
      title={title}
      live={!done}
      testId="wp-patch-run"
      where={
        failedStep ? (
          <>
            <strong>{failedStep.title}</strong> failed.
          </>
        ) : undefined
      }
      actions={
        <>
          <EvidenceActions
            onViewError={failedStep ? () => navigate(runPath) : undefined}
            onFullLogs={() => navigate(runPath)}
            errorTestId="wp-patch-view-error"
            logsTestId="wp-patch-full-logs"
          />
          {done ? (
            <button type="button" className="button button--ghost button--small" onClick={onDismiss}>
              Dismiss
            </button>
          ) : null}
        </>
      }
    >
      <ErrorNotice error={error} />
      {detail ? (
        <SummaryFacts>
          <SummaryFact label="Packages" testId="wp-patch-packages">
            {updates.length === 0 ? 'No update steps' : `${updated}/${updates.length} applied`}
            {status === 'rolled_back' ? ' · Package files restored · Database not rolled back' : ''}
          </SummaryFact>
          {done ? (
            <SummaryFact label="Shop afterwards" testId="wp-patch-shop">
              {shop.text}
            </SummaryFact>
          ) : null}
          {failedStep?.error_message ? (
            <SummaryFact label="Error" testId="wp-patch-error">
              <span className="op-cause">{failedStep.error_message}</span>
            </SummaryFact>
          ) : null}
          {needsNext ? (
            <SummaryFact label="Next step" testId="wp-patch-next">
              <NextSteps steps={nextSteps} unknown="Cause not identified. Start with the failed step’s error, then the full log." />
            </SummaryFact>
          ) : null}
        </SummaryFacts>
      ) : null}
      {detail ? (
        <div className="wp-run__steps" data-testid="wp-patch-steps">
          {attention.length > 0 ? (
            <ol className="wp-run__list">
              {attention.map((step) => (
                <li key={step.id} className={`wp-run__step wp-run__step--${step.status}`}>
                  <span>{step.title}</span>
                  <span className="muted">{labelFor(executionStatusLabel, step.status)}</span>
                </li>
              ))}
            </ol>
          ) : null}
          {quiet.length > 0 ? (
            <details className="quiet-disclosure wp-run__done">
              <summary>
                {quiet.length} {quiet.length === 1 ? 'step' : 'steps'} succeeded or skipped · {actionLabel.viewDetails}
              </summary>
              <ol className="wp-run__list">
                {quiet.map((step) => (
                  <li key={step.id} className={`wp-run__step wp-run__step--${step.status}`}>
                    <span>{step.title}</span>
                    <span className="muted">{step.status === 'succeeded' ? 'Succeeded' : 'Skipped'}</span>
                  </li>
                ))}
              </ol>
            </details>
          ) : null}
        </div>
      ) : null}
    </OperationSummary>
  );
}

function SecurityCard({ findings }: { findings: WordPressSecurityFinding[] }) {
  const project = useProject();
  const security = project.wordpressSite.data?.security;
  const sorted = findings.slice().sort((a, b) => rank(b.severity) - rank(a.severity));

  return (
    <PanelCard labelledBy="wp-security-title" testId="wp-security">
      <SectionHeader
        id="wp-security-title"
        title="Security"
        description={security ? `Hardening score ${security.security_score}/100 · checked ${relativeTime(security.checked_at)}` : undefined}
      />
      {(security?.errors ?? []).length > 0 ? (
        <p className="panel__note muted">Some checks could not run: {security!.errors!.join('; ')}</p>
      ) : null}
      {!security ? (
        <QuietEmpty>Not checked yet · use Check now under Updates</QuietEmpty>
      ) : sorted.length === 0 ? (
        <QuietEmpty testId="wp-no-findings">
          {securityCoverage(project.wordpressSite.data ?? null).complete
            ? 'No findings'
            : 'No findings · not fully checked'}
        </QuietEmpty>
      ) : (
        <ul className="wp-findings">
          {sorted.map((finding) => (
            <li key={finding.id} className="wp-findings__item">
              <div className="wp-findings__head">
                <LevelBadge level={severityLevel[finding.severity] ?? 'unknown'}>{labelFor(securitySeverityLabel, finding.severity)}</LevelBadge>
                <strong>{finding.title}</strong>
                {finding.confirmed ? null : <span className="muted">(not confirmed)</span>}
              </div>
              <p>{finding.description}</p>
              {finding.affected_path ? (
                <p className="muted">
                  <code>{finding.affected_path}</code>
                </p>
              ) : null}
              {finding.remediation ? <p className="wp-findings__fix">{finding.remediation}</p> : null}
            </li>
          ))}
        </ul>
      )}
    </PanelCard>
  );
}

function rank(severity: string): number {
  return { critical: 4, high: 3, medium: 2, low: 1, info: 0 }[severity] ?? 0;
}
